import { checkPermissions, useAuth } from "@elit-hair/elit-tools";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Button,
    Icon,
    SearchBar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    transitions,
} from "@elit-hair/elit-ui";
import * as Sentry from "@sentry/react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbEdit, TbMapPin, TbPlus, TbStar, TbTrash } from "react-icons/tb";
import { z } from "zod";

import { ClinicModal } from "@/components/clinic-modal";
import { PERMISSIONS } from "@/constants/permissions";
import { useClinics, useCreateClinic, useDeleteClinic, useUpdateClinic } from "@/queries/clinic.queries";
import type { Clinic, ClinicQueryParams, CreateClinicRequest } from "@/types/clinic.types";
import { fadeInUp } from "@/utils/animations";

// Search params schema for clinics route
const clinicsSearchSchema = z.object({
    search: z.string().optional(),
    page: z.coerce.number().optional(),
    limit: z.coerce.number().optional(),
    country: z.string().optional(),
    city: z.string().optional(),
});

function ClinicsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { userData } = useAuth();

    // Get search params from URL
    const { search: searchQuery } = useSearch({ from: "/clinics" });
    // Local state for input value (for immediate UI updates)
    const [inputValue, setInputValue] = useState<string>(searchQuery || "");
    const [isClinicModalOpen, setIsClinicModalOpen] = useState<boolean>(false);
    const [editingClinic, setEditingClinic] = useState<Clinic | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [clinicToDelete, setClinicToDelete] = useState<string | null>(null);

    const canCreate = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_CLINICS_CREATE]);
    const canEdit = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_CLINICS_UPDATE]);
    const canDelete = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_CLINICS_DELETE]);

    // Normalize search query: empty string becomes undefined
    const normalizedSearchQuery = searchQuery && searchQuery.trim() ? searchQuery.trim() : undefined;

    // Build query params object, only including defined values
    const queryParams = useMemo((): ClinicQueryParams => {
        const params: ClinicQueryParams = {};
        if (normalizedSearchQuery) {
            params.search = normalizedSearchQuery;
        }
        return params;
    }, [normalizedSearchQuery]);

    // Fetch clinics data
    const { data: clinicsData, isLoading, error } = useClinics(queryParams);

    // Mutations
    const deleteClinicMutation = useDeleteClinic();
    const createClinicMutation = useCreateClinic();
    const updateClinicMutation = useUpdateClinic();

    const handleOpenAddClinicModal = () => {
        setEditingClinic(null);
        setIsClinicModalOpen(true);
    };

    const handleOpenEditClinicModal = (clinic: Clinic) => {
        setEditingClinic(clinic);
        setIsClinicModalOpen(true);
    };

    const handleCloseClinicModal = () => {
        setIsClinicModalOpen(false);
        setEditingClinic(null);
    };

    const handleSubmitClinic = async (data: CreateClinicRequest) => {
        if (editingClinic) {
            await updateClinicMutation.mutateAsync({
                id: editingClinic.id,
                data,
            });
        } else {
            await createClinicMutation.mutateAsync(data);
        }
    };

    const handleDeleteClinic = (clinicId: string) => {
        setClinicToDelete(clinicId);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteClinic = async () => {
        if (!clinicToDelete) return;

        try {
            await deleteClinicMutation.mutateAsync(clinicToDelete);
            setDeleteDialogOpen(false);
            setClinicToDelete(null);
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    component: "ClinicsPage",
                    action: "deleteClinic",
                },
            });
        }
    };

    // Update search param in URL when search changes (debounced)
    const handleDebouncedSearchChange = useCallback(
        (value: string) => {
            const trimmedValue = value.trim();
            navigate({
                to: "/clinics",
                search: {
                    search: trimmedValue || undefined,
                },
                replace: true, // Replace history entry to avoid cluttering browser history
            });
        },
        [navigate]
    );

    // Sync input value with URL search param when it changes externally
    useEffect(() => {
        setInputValue(searchQuery || "");
    }, [searchQuery]);

    return (
        <>
            <div className="min-h-screen p-4">
                <motion.div {...fadeInUp} transition={transitions.slow}>
                    <div className="flex justify-end gap-2">
                        <SearchBar
                            value={inputValue}
                            onChange={setInputValue}
                            onDebouncedChange={handleDebouncedSearchChange}
                            debounceMs={300}
                            placeholder={t("search_clinic")}
                            className="w-96 md:w-[300px]"
                        />
                        {canCreate && (
                            <Button variant="default" onClick={handleOpenAddClinicModal}>
                                <Icon icon={TbPlus} />
                                {t("add_clinic")}
                            </Button>
                        )}
                    </div>
                    <div className="rounded-md">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t("name")}</TableHead>
                                    <TableHead>{t("address")}</TableHead>
                                    <TableHead>{t("google_maps")}</TableHead>
                                    <TableHead>{t("trustpilot")}</TableHead>
                                    <TableHead>{t("actions")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell className="text-center py-8">{t("loading")}...</TableCell>
                                    </TableRow>
                                ) : error ? (
                                    <TableRow>
                                        <TableCell className="text-center py-8 text-red-500">
                                            {t("error_loading_clinics")}
                                        </TableCell>
                                    </TableRow>
                                ) : !clinicsData?.data?.length ? (
                                    <TableRow>
                                        <TableCell className="text-center py-8">{t("no_clinics_found")}</TableCell>
                                    </TableRow>
                                ) : (
                                    clinicsData.data.map((clinic) => (
                                        <TableRow key={clinic.id}>
                                            <TableCell className="font-medium">{clinic.name}</TableCell>
                                            <TableCell>
                                                {clinic.address.street} {clinic.address.number},{" "}
                                                {clinic.address.postalCode} {clinic.address.city},{" "}
                                                {clinic.address.country}
                                            </TableCell>
                                            <TableCell>
                                                {clinic.googleMapsLink ? (
                                                    <a
                                                        href={clinic.googleMapsLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2 hover:underline"
                                                    >
                                                        <Icon icon={TbMapPin} /> {t("view_on_maps")}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {clinic.trustpilotLink ? (
                                                    <a
                                                        href={clinic.trustpilotLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2 hover:underline"
                                                    >
                                                        <Icon icon={TbStar} /> {t("view_reviews")}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {(canEdit || canDelete) && (
                                                    <div className="flex gap-2">
                                                        {canEdit && (
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                onClick={() => handleOpenEditClinicModal(clinic)}
                                                            >
                                                                <Icon icon={TbEdit} />
                                                            </Button>
                                                        )}
                                                        {canDelete && (
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                onClick={() => handleDeleteClinic(clinic.id)}
                                                                disabled={deleteClinicMutation.isPending}
                                                            >
                                                                <Icon icon={TbTrash} />
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </motion.div>
            </div>
            <ClinicModal
                isOpen={isClinicModalOpen}
                onClose={handleCloseClinicModal}
                clinic={editingClinic}
                onSubmit={handleSubmitClinic}
            />
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("this_action_cannot_be_undone")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteClinicMutation.isPending}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteClinic}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteClinicMutation.isPending}
                        >
                            {deleteClinicMutation.isPending ? t("deleting") : t("delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export const Route = createFileRoute("/clinics")({
    component: ClinicsPage,
    validateSearch: (search) => clinicsSearchSchema.parse(search),
});
